import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import axios from "axios";
import { API_URL } from "../api";
import HeaderMenu from "../components/HeaderMenu";
import "../css/StyleMenuHome.css";
import {
  Box,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { NotificationsActive } from "@mui/icons-material";

export const resetAnnouncementSession = () => {
  sessionStorage.removeItem("announcementShown");
};

export default function MenuHome() {
  const [currentTime, setCurrentTime] = useState("");
  const [userName, setUserName] = useState("");
  const [userRole, setUserRole] = useState("");
  const [userJabatan, setUserJabatan] = useState("");
  const [userNik, setUserNik] = useState("");
  const [userMinePermit, setUserMinePermit] = useState("");
  const [attendance, setAttendance] = useState([
    { day: "S", color: "gray" },
    { day: "S", color: "gray" },
    { day: "R", color: "gray" },
    { day: "K", color: "gray" },
    { day: "J", color: "gray" },
    { day: "S", color: "gray" },
    { day: "M", color: "gray" },
  ]);
  const navigate = useNavigate();

  // Announcement popup state
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const [announcementData, setAnnouncementData] = useState({ title: "", message: "" });
  const [announcements, setAnnouncements] = useState([]);
  const [currentAnnouncementIndex, setCurrentAnnouncementIndex] = useState(0);

  // Laporan kerusakan aktif (belum selesai)
  const [laporanAktif, setLaporanAktif] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  // Laporan kerusakan selesai hari ini
  const [laporanSelesai, setLaporanSelesai] = useState([]);

  const fetchKerusakanAktif = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const res = await axios.get(`${API_URL}/alat/kerusakan/aktif`);
      if (res.data?.success) setLaporanAktif(res.data.data || []);
    } catch (err) {
      console.log("Gagal fetch kerusakan aktif:", err);
    } finally {
      if (showRefresh) setRefreshing(false);
    }
  }, []);

  const fetchKerusakanSelesai = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/alat/kerusakan/selesai-hari-ini`);
      if (res.data?.success && res.data.data?.length > 0) {
        setLaporanSelesai(res.data.data);
      } else {
        setLaporanSelesai([]);
      }
    } catch (err) {
      console.log("Gagal fetch kerusakan selesai:", err);
    }
  }, []);

  // Fetch announcement
  const checkAnnouncement = useCallback(async () => {
    if (sessionStorage.getItem("announcementShown")) return;

    try {
      const res = await axios.get(`${API_URL}/announcements/active`);
      if (res.data?.success && res.data.announcements && res.data.announcements.length > 0) {
        sessionStorage.setItem("announcementShown", "true");
        setAnnouncements(res.data.announcements);
        setCurrentAnnouncementIndex(0);
        setAnnouncementData({
          title: res.data.announcements[0].title,
          message: res.data.announcements[0].message,
        });
        setShowAnnouncement(true);
      }
    } catch (err) {
      console.log("[Announcement] Check failed:", err);
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(moment().format("HH:mm"));
    }, 1000);

    const getUserData = () => {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        setUserName(userData.nama || "");
        setUserRole(userData.role || "");
        setUserJabatan(userData.jabatan || "");
        setUserNik(userData.nik || "");
        setUserMinePermit(userData.mine_permit || "");
      } else {
        console.error("Data pengguna tidak ditemukan di localStorage.");
      }
    };

    getUserData();
    checkAnnouncement();
    fetchKerusakanAktif();
    fetchKerusakanSelesai();

    return () => clearInterval(interval);
  }, [checkAnnouncement, fetchKerusakanAktif, fetchKerusakanSelesai]);

  const pollIntervalRef = useRef(null);

  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) return;
    pollIntervalRef.current = setInterval(() => {
      fetchKerusakanAktif();
      fetchKerusakanSelesai();
    }, 60000);
  }, [fetchKerusakanAktif, fetchKerusakanSelesai]);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    startPolling();
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchKerusakanAktif();
        fetchKerusakanSelesai();
        startPolling();
      } else {
        stopPolling();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [fetchKerusakanAktif, fetchKerusakanSelesai, startPolling, stopPolling]);

  useEffect(() => {
    if (!userNik) return;

    const fetchAttendance = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/surveyor/absensi/tanggal/${userNik}`
        );
        const data = response.data;

        const getWeekDates = () => {
          const startOfWeek = moment().startOf("isoWeek");
          return Array.from({ length: 7 }, (_, i) =>
            startOfWeek.clone().add(i, "days").format("YYYY-MM-DD")
          );
        };

        const weekDates = getWeekDates();

        const updatedAttendance = weekDates.map((date) => ({
          day: moment(date).format("dd")[0],
          color: data.tanggal.includes(date) ? "#2dfc3b" : "#c0c4c1",
        }));

        setAttendance(updatedAttendance);
      } catch (error) {
        console.error("Gagal mengambil data absensi:", error);
      }
    };

    fetchAttendance();
  }, [userNik]);

  const handleCloseAnnouncement = () => {
    if (announcements.length > 0 && currentAnnouncementIndex < announcements.length - 1) {
      const nextIndex = currentAnnouncementIndex + 1;
      setCurrentAnnouncementIndex(nextIndex);
      setAnnouncementData({
        title: announcements[nextIndex].title,
        message: announcements[nextIndex].message,
      });
    } else {
      setShowAnnouncement(false);
      setAnnouncements([]);
      setCurrentAnnouncementIndex(0);
    }
  };

  const fmtDate = (isoStr) => {
    if (!isoStr) return null;
    return moment(isoStr).format("DD MMM YYYY, HH:mm");
  };

  const getTanggalTahapan = (item) => {
    if (item.tanggal_mulai) return { label: "Mulai Maintenance", value: fmtDate(item.tanggal_mulai) };
    if (item.tanggal_terima) return { label: "Diterima", value: fmtDate(item.tanggal_terima) };
    return { label: "Dilaporkan", value: fmtDate(item.created_at) };
  };

  const StatusChip = ({ status }) => {
    const cfg = {
      menunggu: { label: "Menunggu", bg: "#fef9c3", text: "#92400e" },
      diterima: { label: "Diterima", bg: "#dcfce7", text: "#166534" },
      maintenance: { label: "Maintenance", bg: "#ffedd5", text: "#9a3412" },
      selesai: { label: "Selesai", bg: "#dbeafe", text: "#1e40af" },
    };
    const c = cfg[status?.toLowerCase()] || { label: status || "-", bg: "#f3f4f6", text: "#374151" };
    return (
      <Box
        sx={{
          backgroundColor: c.bg,
          color: c.text,
          borderRadius: "20px",
          px: 1.5,
          py: 0.5,
          fontSize: "11px",
          fontWeight: "bold",
          textTransform: "capitalize",
          display: "inline-block",
        }}
      >
        {c.label}
      </Box>
    );
  };

  return (
    <div className="menu-home-container" style={{ width: "100%", overflowX: 'hidden' }}>
      <HeaderMenu name={userName} position={userJabatan} />
      <div className="menu-home-content" style={{ paddingBottom: '30px' }}>
        <div className="absen-card">
          <div className="date-time-row">
            <div className="date-text">
              {moment().format("DD")}{" "}
              <span className="bold-text">{moment().format("MMMM YYYY")}</span>
            </div>
            <div className="time-text">{currentTime}</div>
          </div>
          <div className="absen-title">Absensi Minggu Ini</div>
          <div className="attendance-row">
            {attendance.map((item, index) => (
              <div key={index} className="attendance-item">
                <div>{item.day}</div>
                <div
                  className="status-dot"
                  style={{ backgroundColor: item.color }}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="menu-grid">
          {userRole === "admin" && (
            <div className="menu-item" onClick={() => navigate("/kehadiran")}>
              <button className="menu-button">
                <span className="mdi mdi-account-group menu-icon"></span>
              </button>
              <div className="menu-text">Kehadiran P5M</div>
            </div>
          )}
          <div className="menu-item" onClick={() => navigate("/menu-absensi")}>
            <button className="menu-button">
              <span className="mdi mdi-clock-check menu-icon"></span>
            </button>
            <div className="menu-text">Absensi P5M</div>
          </div>

          <div
            className="menu-item"
            onClick={() => navigate("/form-laporan-harian")}
          >
            <button className="menu-button">
              <span className="mdi mdi-text-box-plus menu-icon"></span>
            </button>
            <div className="menu-text">Laporan Harian</div>
          </div>

          <div className="menu-item" onClick={() => navigate("/menu-alat")}>
            <button className="menu-button">
              <span className="mdi mdi-toolbox menu-icon"></span>
            </button>
            <div className="menu-text">Alat</div>
          </div>

          <div
            className="menu-item"
            onClick={() => navigate("/laporan-safety")}
          >
            <button className="menu-button">
              <span className="mdi mdi-shield-check-outline menu-icon"></span>
            </button>
            <div className="menu-text">Laporan Safety</div>
          </div>

          <div
            className="menu-item"
            onClick={() => navigate("/menu-riwayat-laporan")}
          >
            <button className="menu-button">
              <span className="mdi mdi-clipboard-text-clock menu-icon"></span>
            </button>
            <div className="menu-text">Riwayat Laporan</div>
          </div>
        </div>

        {/* ── Laporan Kerusakan Aktif ───────────────────────────────────── */}
        {laporanAktif.length > 0 && (
          <Box sx={{ mx: 'auto', px: 2, mt: 4, mb: 2, maxWidth: '800px', width: '100%', boxSizing: 'border-box' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 'bold', color: '#1f2937' }}>🔧 Update Kerusakan</Typography>
                <Typography sx={{ fontSize: 12, color: '#9ca3af' }}>{laporanAktif.length} laporan belum selesai</Typography>
              </Box>
              <Box
                onClick={() => !refreshing && fetchKerusakanAktif(true)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#fff7ed',
                  borderRadius: '20px',
                  px: 1.5,
                  py: 0.5,
                  border: '1px solid #fed7aa',
                  cursor: 'pointer',
                  minWidth: '80px',
                  justifyContent: 'center'
                }}
              >
                {refreshing ? (
                  <CircularProgress size={14} sx={{ color: '#e67e22' }} />
                ) : (
                  <Typography sx={{ fontSize: 12, color: '#e67e22', fontWeight: 600 }}>🔄 Refresh</Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', overflowX: 'auto', pb: 1, '&::-webkit-scrollbar': { display: 'none' } }}>
              {laporanAktif.map((item) => {
                const tahapan = getTanggalTahapan(item);
                const stepOrder = ['menunggu', 'diterima', 'maintenance'];
                const statusNow = (item.status || 'menunggu').toLowerCase();
                const currentIdx = stepOrder.indexOf(statusNow);

                return (
                  <Box
                    key={item.id}
                    onClick={() => navigate(`/detail/kerusakan/${item.id}`)}
                    sx={{
                      backgroundColor: '#fff',
                      borderRadius: '14px',
                      p: 2,
                      mr: 2,
                      minWidth: '260px',
                      maxWidth: '260px',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                      borderLeft: '4px solid #e67e22',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ flex: 1, mr: 1, overflow: 'hidden' }}>
                        <Typography noWrap sx={{ fontSize: 14, fontWeight: 'bold', color: '#111827' }}>
                          {item.nama_alat}
                        </Typography>
                        <Typography noWrap sx={{ fontSize: 11, color: '#9ca3af' }}>
                          SN: {item.serial_number}
                        </Typography>
                      </Box>
                      <StatusChip status={item.status} />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <Typography sx={{ fontSize: 13, mr: 0.5 }}>📍</Typography>
                      <Typography noWrap sx={{ fontSize: 12, color: '#4b5563', flex: 1 }}>{item.lokasi}</Typography>
                    </Box>

                    <Box sx={{ height: '1px', backgroundColor: '#f3f4f6', my: 1 }} />

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: 13, mr: 0.5 }}>🕐</Typography>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 11, color: '#6b7280' }}>{tahapan.label}</Typography>
                        <Typography sx={{ fontSize: 11, color: '#1f2937', fontWeight: 600 }}>{tahapan.value}</Typography>
                      </Box>
                    </Box>

                    {/* Stepper visual */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5, px: 0.5 }}>
                      {stepOrder.map((s, i) => {
                        const done = i <= currentIdx;
                        return (
                          <React.Fragment key={s}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: done ? '#e67e22' : '#d1d5db' }} />
                            {i < 2 && (
                              <Box sx={{ flex: 1, height: 2, backgroundColor: done && i < currentIdx ? '#e67e22' : '#d1d5db' }} />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                      {['Menunggu', 'Diterima', 'Maintenance'].map((l) => (
                        <Typography key={l} sx={{ fontSize: 9, color: '#9ca3af', width: '30%', textAlign: 'center' }}>
                          {l}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* ── Laporan Selesai Hari Ini ─────────────────────────────────── */}
        {laporanSelesai.length > 0 && (
          <Box sx={{ mx: 'auto', px: 2, mt: 2, mb: 4, maxWidth: '800px', width: '100%', boxSizing: 'border-box' }}>
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 'bold', color: '#166534' }}>✅ Selesai Diperbaiki</Typography>
              <Typography sx={{ fontSize: 12, color: '#9ca3af' }}>{laporanSelesai.length} alat siap diambil di HO hari ini</Typography>
            </Box>

            <Box sx={{ display: 'flex', overflowX: 'auto', pb: 1, '&::-webkit-scrollbar': { display: 'none' } }}>
              {laporanSelesai.map((item) => (
                <Box
                  key={`done-${item.id}`}
                  onClick={() => navigate(`/detail/kerusakan/${item.id}`)}
                  sx={{
                    backgroundColor: '#f0fdf4',
                    borderRadius: '14px',
                    p: 2,
                    mr: 2,
                    minWidth: '260px',
                    maxWidth: '260px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.07)',
                    borderLeft: '4px solid #22c55e',
                    cursor: 'pointer',
                    flexShrink: 0
                  }}
                >
                  <Box sx={{ backgroundColor: '#dcfce7', borderRadius: '20px', px: 1.5, py: 0.5, display: 'inline-block', mb: 1 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 'bold', color: '#166534' }}>✅ SELESAI</Typography>
                  </Box>

                  <Typography noWrap sx={{ fontSize: 14, fontWeight: 'bold', color: '#111827' }}>{item.nama_alat}</Typography>
                  <Typography noWrap sx={{ fontSize: 11, color: '#9ca3af' }}>SN: {item.serial_number}</Typography>

                  <Box sx={{ height: '1px', backgroundColor: '#e5e7eb', my: 1 }} />

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Typography sx={{ fontSize: 13, mr: 0.5 }}>📍</Typography>
                    <Typography noWrap sx={{ fontSize: 12, color: '#4b5563', flex: 1 }}>{item.lokasi}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Typography sx={{ fontSize: 13, mr: 0.5 }}>🕐</Typography>
                    <Typography sx={{ fontSize: 11, color: '#1f2937', fontWeight: 600, flex: 1 }}>
                      {item.tanggal_selesai ? moment(item.tanggal_selesai).format('DD MMM YYYY, HH:mm') : '-'}
                    </Typography>
                  </Box>

                  {item.nama_selesai && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: 13, mr: 0.5 }}>👤</Typography>
                      <Typography noWrap sx={{ fontSize: 12, color: '#4b5563', fontWeight: 600, flex: 1 }}>
                        {item.nama_selesai}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ mt: 1.5, backgroundColor: '#166534', borderRadius: '8px', py: 0.75, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 11, color: '#fff', fontWeight: 'bold' }}>🏢 Siap diambil di HO</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

      </div>

      {/* Announcement Dialog */}
      <Dialog
        open={showAnnouncement}
        onClose={handleCloseAnnouncement}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { borderRadius: 16 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', pb: 1, justifyContent: 'center' }}>
          <NotificationsActive color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6" fontWeight="bold">Pemberitahuan</Typography>
        </DialogTitle>
        <DialogContent sx={{ pb: 2, justifyContent: 'center', textAlign: 'center' }}>
          {announcements.length > 1 && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
              Pesan {currentAnnouncementIndex + 1} dari {announcements.length}
            </Typography>
          )}
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            {announcementData.title}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
            {announcementData.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={handleCloseAnnouncement}
            variant="contained"
            color="primary"
            fullWidth
            sx={{ borderRadius: 8, textTransform: 'none', fontWeight: 'bold' }}
          >
            {announcements.length > 1 && currentAnnouncementIndex < announcements.length - 1
              ? "Selanjutnya"
              : "Tutup"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
